import { Loader2, Pause, Volume2, VolumeX } from "lucide-react";
import { useLumiVoice } from "@/hooks/useLumiVoice";
import { cn } from "@/lib/utils";

interface Props {
  text: string;
  contextId?: string | null;
  label?: string;
  /** Compact icon-only pill (default) vs. icon + label. */
  variant?: "icon" | "labeled";
  className?: string;
  /** Allow learner-initiated live TTS for static allowlisted lines or verified Explore summaries. */
  allowGenerate?: boolean;
  /** Dedicated verified Explore summary mode. */
  mode?: "static" | "explore-summary";
  summaryId?: string | null;
}

/**
 * Small, accessible play/pause button that speaks short text in Lumi's voice.
 * Voice is always optional — text remains the source of truth in the UI.
 */
export function LumiVoiceButton({ text, contextId, label = "Hear Lumi read this", variant = "icon", className, allowGenerate, mode = "static", summaryId }: Props) {
  const { status, play } = useLumiVoice();
  const disabled = !text || !text.trim();

  const Icon = status === "loading" || status === "generating" ? Loader2 : status === "playing" ? Pause : status === "unavailable" || status === "failed" ? VolumeX : Volume2;
  const unavailable = status === "unavailable" || status === "failed";
  const stateLabel =
    status === "generating"
      ? "Generating Lumi’s voice"
      : status === "loading"
      ? "Loading Lumi’s voice"
      : status === "playing"
      ? "Stop Lumi audio"
      : status === "unavailable"
      ? "Lumi audio unavailable in this browser"
      : status === "failed"
      ? "Lumi audio failed to play"
      : `Play ${label}`;

  const handleClick = () => {
    if (mode === "explore-summary") {
      console.log("Explore Lumi audio clicked", {
        lessonId: contextId,
        source: "explore",
        mode,
        allowGenerate: allowGenerate === true,
        textPreview: text?.slice(0, 80),
      });
    }
    void play(text, { contextId, allowGenerate, mode, summaryId });
  };

  return (
    <button
      type="button"
      aria-label={stateLabel}
      aria-live="polite"
      aria-disabled={disabled || unavailable}
      title={stateLabel}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary transition-all hover:border-primary/45 hover:bg-primary/15 disabled:opacity-40 sm:text-[11px]",
        status === "playing" && "border-primary/60 bg-primary/20",
        (status === "unavailable" || status === "failed") && "border-muted-foreground/30 bg-muted/20 text-muted-foreground",
        className,
      )}
    >
      <Icon className={cn("h-3 w-3", (status === "loading" || status === "generating") && "animate-spin")} />
      {variant === "labeled" && (
        <span>{status === "playing" ? "Playing" : status === "generating" ? "Generating" : status === "loading" ? "Loading" : status === "unavailable" ? "Unavailable" : "Lumi"}</span>
      )}
    </button>
  );
}